import { useEffect, useRef } from 'react';

/**
 * Hook that adds a fade-up entrance animation when an element scrolls into view.
 * Uses IntersectionObserver for performance.
 * 
 * @param options - threshold (0-1), delay in ms, disabled flag
 * @returns ref to attach to the element
 */
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(
  options?: { threshold?: number; delay?: number; disabled?: boolean }
) {
  const ref = useRef<T>(null);
  const { threshold = 0.15, delay = 0, disabled = false } = options || {};

  useEffect(() => {
    const el = ref.current;
    if (!el || disabled) return;

    // Respect users who prefer reduced motion — no entrance animation.
    if (typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    // Set initial hidden state
    el.style.opacity = '0';
    el.style.transform = 'translateY(24px)';
    el.style.transition = `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms`;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.unobserve(el);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, delay, disabled]);

  return ref;
}
